"use strict";
import { NativeCurrency } from "@uniswap/sdk-core";
import { useTranslation } from "react-i18next";
import { getAlertColor } from "components/modals/WarningModal/getAlertColor";
import { WarningSeverity } from "components/modals/WarningModal/types";
import { ProtectionResult } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { AttackType, TokenList } from "features/dataApi/types";
import { useLocalizationContext } from "features/language/LocalizationContext";
import { isInterface } from "utilities/src/platform";
export var TokenProtectionWarning = /* @__PURE__ */ ((TokenProtectionWarning2) => {
  TokenProtectionWarning2[TokenProtectionWarning2["Blocked"] = 10] = "Blocked";
  TokenProtectionWarning2[TokenProtectionWarning2["MaliciousHoneypot"] = 9] = "MaliciousHoneypot";
  TokenProtectionWarning2[TokenProtectionWarning2["FotVeryHigh"] = 8] = "FotVeryHigh";
  TokenProtectionWarning2[TokenProtectionWarning2["MaliciousImpersonator"] = 7] = "MaliciousImpersonator";
  TokenProtectionWarning2[TokenProtectionWarning2["FotHigh"] = 6] = "FotHigh";
  TokenProtectionWarning2[TokenProtectionWarning2["MaliciousGeneral"] = 5] = "MaliciousGeneral";
  TokenProtectionWarning2[TokenProtectionWarning2["PotentialHoneypot"] = 4.5] = "PotentialHoneypot";
  TokenProtectionWarning2[TokenProtectionWarning2["SpamAirdrop"] = 4] = "SpamAirdrop";
  TokenProtectionWarning2[TokenProtectionWarning2["FotLow"] = 3] = "FotLow";
  TokenProtectionWarning2[TokenProtectionWarning2["NonDefault"] = 2] = "NonDefault";
  TokenProtectionWarning2[TokenProtectionWarning2["None"] = 1] = "None";
  return TokenProtectionWarning2;
})(TokenProtectionWarning || {});
export const TOKEN_PROTECTION_FOT_HONEYPOT_BREAKPOINT = 100;
export const TOKEN_PROTECTION_FOT_HIGH_FEE_BREAKPOINT = 80;
export const TOKEN_PROTECTION_FOT_FEE_BREAKPOINT = 15;
export function getCurrencyFeeOnTransfer(currency) {
  if (!currency) {
    return {
      buyFeePercent: void 0,
      sellFeePercent: void 0
    };
  }
  if (currency.isNative) {
    return {
      buyFeePercent: 0,
      sellFeePercent: 0
    };
  }
  const sellFeePercent = currency.sellFeeBps ? currency.sellFeeBps.toNumber() / 100 : void 0;
  const buyFeePercent = currency.buyFeeBps ? currency.buyFeeBps.toNumber() / 100 : void 0;
  return {
    buyFeePercent,
    sellFeePercent
  };
}
export function getTokenProtectionFeeOnTransfer(currencyInfo) {
  const { buyFeePercent, sellFeePercent } = getCurrencyFeeOnTransfer(currencyInfo?.currency);
  const blockaidFeesData = currencyInfo?.safetyInfo?.blockaidFees;
  const displayedBuyFeePercent = buyFeePercent ?? blockaidFeesData?.buyFeePercent;
  const displayedSellFeePercent = sellFeePercent ?? blockaidFeesData?.sellFeePercent;
  return {
    buyFeePercent: displayedBuyFeePercent,
    sellFeePercent: displayedSellFeePercent,
    maxFeePercent: displayedSellFeePercent || displayedBuyFeePercent ? Math.max(displayedSellFeePercent ?? 0, displayedBuyFeePercent ?? 0) : void 0
  };
}
export function getTokenProtectionWarning(currencyInfo) {
  if (!currencyInfo?.currency || !currencyInfo?.safetyInfo) {
    return 2 /* NonDefault */;
  }
  const { currency, safetyInfo } = currencyInfo;
  const { protectionResult, attackType } = safetyInfo;
  if (currency instanceof NativeCurrency) {
    return 1 /* None */;
  }
  const { maxFeePercent: feeOnTransfer } = getTokenProtectionFeeOnTransfer(currencyInfo);
  if (safetyInfo.tokenList === TokenList.Blocked) {
    return 10 /* Blocked */;
  } else if (feeOnTransfer === TOKEN_PROTECTION_FOT_HONEYPOT_BREAKPOINT) {
    return 9 /* MaliciousHoneypot */;
  } else if (feeOnTransfer && feeOnTransfer >= TOKEN_PROTECTION_FOT_HIGH_FEE_BREAKPOINT || (protectionResult === ProtectionResult.Malicious || protectionResult === ProtectionResult.Spam) && attackType === AttackType.HighFees) {
    return 8 /* FotVeryHigh */;
  } else if ((protectionResult === ProtectionResult.Malicious || protectionResult === ProtectionResult.Spam) && attackType === AttackType.Impersonator) {
    return 7 /* MaliciousImpersonator */;
  } else if (feeOnTransfer && feeOnTransfer >= TOKEN_PROTECTION_FOT_FEE_BREAKPOINT) {
    return 6 /* FotHigh */;
  } else if (protectionResult === ProtectionResult.Malicious) {
    return 5 /* MaliciousGeneral */;
  } else if (attackType === AttackType.Honeypot) {
    return 4.5 /* PotentialHoneypot */;
  } else if (protectionResult === ProtectionResult.Spam && attackType === AttackType.Airdrop) {
    return 4 /* SpamAirdrop */;
  } else if (feeOnTransfer && feeOnTransfer > 0 && feeOnTransfer < TOKEN_PROTECTION_FOT_FEE_BREAKPOINT) {
    return 3 /* FotLow */;
  } else if (safetyInfo.tokenList === TokenList.NonDefault) {
    return 2 /* NonDefault */;
  }
  return 1 /* None */;
}
export function getIsFeeRelatedWarning(tokenProtectionWarning) {
  return tokenProtectionWarning === 9 /* MaliciousHoneypot */ || tokenProtectionWarning === 8 /* FotVeryHigh */ || tokenProtectionWarning === 6 /* FotHigh */ || tokenProtectionWarning === 3 /* FotLow */;
}
export function getFeeWarning(feePercent) {
  let tokenProtectionWarning = 1 /* None */;
  if (feePercent >= TOKEN_PROTECTION_FOT_HONEYPOT_BREAKPOINT) {
    tokenProtectionWarning = 9 /* MaliciousHoneypot */;
  } else if (feePercent >= TOKEN_PROTECTION_FOT_HIGH_FEE_BREAKPOINT) {
    tokenProtectionWarning = 8 /* FotVeryHigh */;
  } else if (feePercent >= TOKEN_PROTECTION_FOT_FEE_BREAKPOINT) {
    tokenProtectionWarning = 6 /* FotHigh */;
  } else if (feePercent > 0) {
    tokenProtectionWarning = 3 /* FotLow */;
  }
  return tokenProtectionWarning;
}
export function getTokenWarningSeverity(currencyInfo) {
  if (!currencyInfo) {
    return WarningSeverity.None;
  }
  const tokenProtectionWarning = getTokenProtectionWarning(currencyInfo);
  return getSeverityFromTokenProtectionWarning(tokenProtectionWarning);
}
export function getSeverityFromTokenProtectionWarning(tokenProtectionWarning) {
  switch (tokenProtectionWarning) {
    case 10 /* Blocked */:
      return WarningSeverity.Blocked;
    case 9 /* MaliciousHoneypot */:
    case 7 /* MaliciousImpersonator */:
    case 5 /* MaliciousGeneral */:
    case 8 /* FotVeryHigh */:
    case 6 /* FotHigh */:
      return WarningSeverity.High;
    case 4.5 /* PotentialHoneypot */:
    case 4 /* SpamAirdrop */:
    case 3 /* FotLow */:
      return WarningSeverity.Medium;
    case 2 /* NonDefault */:
      return WarningSeverity.Low;
    case 1 /* None */:
      return WarningSeverity.None;
  }
}
export function getShouldHaveCombinedPluralTreatment(currencyInfo0, currencyInfo1) {
  const designTreatment0 = getTokenWarningSeverity(currencyInfo0);
  const designTreatment1 = getTokenWarningSeverity(currencyInfo1);
  const pluralLowWarnings = currencyInfo1 && designTreatment0 === WarningSeverity.Low && designTreatment1 === WarningSeverity.Low;
  const pluralBlockedWarnings = currencyInfo1 && designTreatment0 === WarningSeverity.Blocked && designTreatment1 === WarningSeverity.Blocked;
  const plural = pluralLowWarnings || pluralBlockedWarnings;
  return plural ?? false;
}
export function useModalHeaderText({
  tokenProtectionWarning,
  tokenSymbol0,
  tokenSymbol1,
  shouldHavePluralTreatment
}) {
  const { t } = useTranslation();
  if (!tokenProtectionWarning) {
    return null;
  }
  if (!shouldHavePluralTreatment && tokenSymbol1) {
    throw new Error("Should only combine into one plural-languaged modal if BOTH are low or BOTH are blocked");
  }
  switch (tokenProtectionWarning) {
    case 10 /* Blocked */:
      return shouldHavePluralTreatment && tokenSymbol1 ? t("token.safety.blocked.title.tokensNotAvailable", {
        tokenSymbol0: tokenSymbol0 ?? t("common.thisToken"),
        tokenSymbol1
      }) : t("token.safety.blocked.title.tokenNotAvailable", { tokenSymbol: tokenSymbol0 ?? t("common.thisToken") });
    case 9 /* MaliciousHoneypot */:
      return t("token.safety.warning.sellFee100.title");
    case 8 /* FotVeryHigh */:
      return t("token.safety.warning.fotVeryHigh.title");
    case 7 /* MaliciousImpersonator */:
      return t("token.safety.warning.impersonator.title");
    case 6 /* FotHigh */:
      return t("token.safety.warning.fotHigh.title");
    case 5 /* MaliciousGeneral */:
      return t("token.safety.warning.malicious.title");
    case 4.5 /* PotentialHoneypot */:
      return t("token.safety.warning.potentialHoneypot.title");
    case 4 /* SpamAirdrop */:
      return t("token.safety.warning.spam.title");
    case 3 /* FotLow */:
      return t("token.safety.warning.fotLow.title");
    case 2 /* NonDefault */:
      return t("token.safety.warning.alwaysDoYourResearch");
    case 1 /* None */:
      return null;
  }
}
export function useModalSubtitleText({
  tokenProtectionWarning,
  tokenSymbol,
  buyFeePercent,
  sellFeePercent,
  shouldHavePluralTreatment
}) {
  const { formatPercent } = useLocalizationContext();
  const { t } = useTranslation();
  if (!tokenProtectionWarning) {
    return null;
  }
  const tokenSymbolWithFallback = tokenSymbol ?? t("common.thisToken");
  const formattedBuyFeePercent = buyFeePercent && buyFeePercent > 0 ? formatPercent(buyFeePercent) : void 0;
  const formattedSellFeePercent = sellFeePercent && sellFeePercent > 0 ? formatPercent(sellFeePercent) : void 0;
  switch (tokenProtectionWarning) {
    case 10 /* Blocked */:
      return isInterface ? shouldHavePluralTreatment ? t("token.safety.warning.blocked.description.default_other") : t("token.safety.warning.blocked.description.default_one") : t("token.safetyLevel.blocked.message");
    case 9 /* MaliciousHoneypot */:
      return t("token.safety.warning.honeypot.message", { tokenSymbol: tokenSymbolWithFallback });
    case 5 /* MaliciousGeneral */:
      return t("token.safety.warning.malicious.general.message", { tokenSymbol: tokenSymbolWithFallback }) + " " + t("token.safety.warning.doYourOwnResearch");
    case 7 /* MaliciousImpersonator */:
      return t("token.safety.warning.malicious.impersonator.message", { tokenSymbol: tokenSymbolWithFallback });
    case 4.5 /* PotentialHoneypot */:
      return t("token.safety.warning.potentialHoneypot.modal.message", { tokenSymbol: tokenSymbolWithFallback });
    case 4 /* SpamAirdrop */:
      return t("token.safety.warning.spam.message", { tokenSymbol: tokenSymbolWithFallback }) + " " + t("token.safety.warning.doYourOwnResearch");
    case 8 /* FotVeryHigh */:
    case 6 /* FotHigh */:
    case 3 /* FotLow */: {
      const feePercentCopy = !!formattedBuyFeePercent && !!formattedSellFeePercent ? t("token.safety.warning.tokenChargesFee.both.message", {
        tokenSymbol: tokenSymbolWithFallback,
        buyFeePercent: formattedBuyFeePercent,
        sellFeePercent: formattedSellFeePercent
      }) : formattedBuyFeePercent ? t("token.safety.warning.tokenChargesFee.buy.message", {
        tokenSymbol: tokenSymbolWithFallback,
        feePercent: formattedBuyFeePercent
      }) : formattedSellFeePercent ? t("token.safety.warning.tokenChargesFee.sell.message", {
        tokenSymbol: tokenSymbolWithFallback,
        feePercent: formattedSellFeePercent
      }) : t("token.safety.warning.tokenChargesFee.unknownFee.message", {
        tokenSymbol: tokenSymbolWithFallback
      });
      return feePercentCopy + " " + t("token.safety.warning.mayResultInLoss") + " " + t("token.safety.fees.uniswapLabsDoesNotReceive");
    }
    case 2 /* NonDefault */:
      return shouldHavePluralTreatment ? t("token.safetyLevel.medium.message.plural") : t("token.safety.warning.medium.heading.named", { tokenSymbol: tokenSymbolWithFallback });
    case 1 /* None */:
      return null;
  }
}
export function useTokenWarningCardText(currencyInfo) {
  const tokenProtectionWarning = getTokenProtectionWarning(currencyInfo);
  const { buyFeePercent, sellFeePercent } = getTokenProtectionFeeOnTransfer(currencyInfo);
  const heading = useCardHeaderText({ tokenProtectionWarning });
  const description = useCardSubtitleText({
    tokenProtectionWarning,
    tokenSymbol: currencyInfo?.currency.symbol,
    buyFeePercent,
    sellFeePercent
  });
  if (!currencyInfo || !currencyInfo?.safetyInfo) {
    return {
      heading: null,
      description: null
    };
  }
  return {
    heading,
    description
  };
}
export function useCardHeaderText({
  tokenProtectionWarning
}) {
  const { t } = useTranslation();
  switch (tokenProtectionWarning) {
    case 9 /* MaliciousHoneypot */:
      return t("token.safety.warning.honeypot.title");
    case 8 /* FotVeryHigh */:
      return t("token.safety.warning.fotVeryHigh.title");
    case 7 /* MaliciousImpersonator */:
      return t("token.safety.warning.impersonator.title");
    case 6 /* FotHigh */:
      return t("token.safety.warning.fotHigh.title");
    case 5 /* MaliciousGeneral */:
      return t("token.safety.warning.malicious.title");
    case 4.5 /* PotentialHoneypot */:
      return t("token.safety.warning.potentialHoneypot.title");
    case 4 /* SpamAirdrop */:
      return t("token.safety.warning.spam.title");
    case 3 /* FotLow */:
      return t("token.safety.warning.fotLow.title");
    case 2 /* NonDefault */:
    case 1 /* None */:
    default:
      return null;
  }
}
export function useCardSubtitleText({
  tokenProtectionWarning,
  tokenSymbol,
  buyFeePercent,
  sellFeePercent
}) {
  const { t } = useTranslation();
  const { formatPercent } = useLocalizationContext();
  const tokenSymbolWithFallback = tokenSymbol ?? t("common.thisToken");
  const formattedBuyFeePercent = buyFeePercent && buyFeePercent > 0 ? formatPercent(buyFeePercent) : void 0;
  const formattedSellFeePercent = sellFeePercent && sellFeePercent > 0 ? formatPercent(sellFeePercent) : void 0;
  switch (tokenProtectionWarning) {
    case 10 /* Blocked */:
      return isInterface ? t("token.safety.warning.blocked.description.default_one") : t("token.safetyLevel.blocked.message");
    case 9 /* MaliciousHoneypot */:
      return t("token.safety.warning.sellFee100.message", { tokenSymbol: tokenSymbolWithFallback });
    case 5 /* MaliciousGeneral */:
      return t("token.safety.warning.malicious.general.message", { tokenSymbol: tokenSymbolWithFallback });
    case 7 /* MaliciousImpersonator */:
      return t("token.safety.warning.malicious.impersonator.message.short", { tokenSymbol: tokenSymbolWithFallback });
    case 4.5 /* PotentialHoneypot */:
      return t("token.safety.warning.potentialHoneypot.card.message", { tokenSymbol: tokenSymbolWithFallback });
    case 4 /* SpamAirdrop */:
      return t("token.safety.warning.spam.message", { tokenSymbol: tokenSymbolWithFallback });
    case 8 /* FotVeryHigh */:
    case 6 /* FotHigh */:
    case 3 /* FotLow */: {
      const feePercentCopy = !!formattedBuyFeePercent && !!formattedSellFeePercent ? t("token.safety.warning.tokenChargesFee.both.message", {
        tokenSymbol: tokenSymbolWithFallback,
        buyFeePercent: formattedBuyFeePercent,
        sellFeePercent: formattedSellFeePercent
      }) : formattedBuyFeePercent ? t("token.safety.warning.tokenChargesFee.buy.message", {
        tokenSymbol: tokenSymbolWithFallback,
        feePercent: formattedBuyFeePercent
      }) : formattedSellFeePercent ? t("token.safety.warning.tokenChargesFee.sell.message", {
        tokenSymbol: tokenSymbolWithFallback,
        feePercent: formattedSellFeePercent
      }) : t("token.safety.warning.tokenChargesFee.unknownFee.message", {
        tokenSymbol: tokenSymbolWithFallback
      });
      return feePercentCopy;
    }
    case 2 /* NonDefault */:
      return t("token.safety.warning.medium.heading.named", { tokenSymbol: tokenSymbolWithFallback });
    case 1 /* None */:
      return null;
  }
}
export function getFeeColor(feePercent) {
  const tokenProtectionWarning = getFeeWarning(feePercent);
  const severity = getSeverityFromTokenProtectionWarning(tokenProtectionWarning);
  const { headerText: textColor } = getAlertColor(severity);
  return textColor;
}
